import { useTranslation } from 'react-i18next';

interface ValidationSample {
  label: string;
  source_timestamp: string;
  source_energy_kwh?: number;
  source_price_usd_mwh?: number;
  db_timestamp: string;
  db_energy_kwh?: number;
  db_price_usd_mwh?: number;
  match: boolean;
}

interface DSTValidationItem {
  label: string;
  status: string;
  source_count: number;
  db_count: number;
}

interface ValidationData {
  summary?: {
    source_records: number;
    db_records: number;
    match: boolean;
  };
  samples?: ValidationSample[];
  dst_validation?: Record<string, DSTValidationItem>;
}

interface ValidationTableProps {
  validation: ValidationData | null;
  type: 'consumption' | 'price' | 'bill';
}

export function ValidationTable({ validation, type }: ValidationTableProps) {
  const { t } = useTranslation();

  if (!validation) return null;

  const getValueColumnHeader = () => {
    switch (type) {
      case 'consumption':
        return t('consumptionUpload.validation.fileKwh');
      case 'price':
        return t('priceUpload.validation.filePriceMWh');
      default:
        return t('validation.value');
    }
  };

  const getDbValueColumnHeader = () => {
    switch (type) {
      case 'consumption':
        return t('consumptionUpload.validation.dbKwh');
      case 'price':
        return t('priceUpload.validation.dbPriceMWh');
      default:
        return t('validation.dbValue');
    }
  };

  const renderValueCell = (sample: ValidationSample) => {
    if (type === 'consumption') {
      return sample.source_energy_kwh?.toFixed(1);
    } else if (type === 'price') {
      return sample.source_price_usd_mwh?.toFixed(2);
    }
    return '-';
  };

  const renderDbValueCell = (sample: ValidationSample) => {
    if (type === 'consumption') {
      return sample.db_energy_kwh?.toFixed(1);
    } else if (type === 'price') {
      return sample.db_price_usd_mwh?.toFixed(2);
    }
    return '-';
  };

  return (
    <div className="space-y-4 p-4 bg-swiss-pearl/40 rounded-lg border border-swiss-mist">
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-swiss-mist"></div>
        <div className="text-sm font-semibold text-swiss-stone uppercase tracking-wide">
          {t(`${type}Upload.validation.title`)}
        </div>
        <div className="h-px flex-1 bg-swiss-mist"></div>
      </div>

      {/* Summary */}
      <div className="p-3 bg-white rounded border border-swiss-mist">
        <div className="text-xs text-swiss-charcoal">
          <span className="font-medium">{t(`${type}Upload.validation.records`)}:</span>{' '}
          {validation.summary?.source_records} {t(`${type}Upload.validation.uploaded`)} →{' '}
          {validation.summary?.db_records} {t(`${type}Upload.validation.inDatabase`)}
          {validation.summary?.match && (
            <span className="ml-2 text-brand-primary font-semibold">
              ✓ {t(`${type}Upload.validation.allMatched`)}
            </span>
          )}
        </div>
      </div>

      {/* Sample Rows Table */}
      {validation.samples && validation.samples.length > 0 && (
        <div className="overflow-x-auto rounded border border-swiss-mist">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-swiss-mist bg-white">
                <th className="px-3 py-2 text-left text-swiss-stone font-medium">
                  {t(`${type}Upload.validation.sample`)}
                </th>
                <th className="px-3 py-2 text-left text-swiss-stone font-medium">
                  {t(`${type}Upload.validation.fileTimestamp`)}
                </th>
                <th className="px-3 py-2 text-right text-swiss-stone font-medium">
                  {getValueColumnHeader()}
                </th>
                <th className="px-3 py-2 text-left text-swiss-stone font-medium">
                  {t(`${type}Upload.validation.dbTimestamp`)}
                </th>
                <th className="px-3 py-2 text-right text-swiss-stone font-medium">
                  {getDbValueColumnHeader()}
                </th>
                <th className="px-3 py-2 text-center text-swiss-stone font-medium">✓</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {validation.samples.map((sample, idx) => (
                <tr
                  key={idx}
                  className="border-b border-swiss-mist last:border-0 hover:bg-swiss-pearl/20"
                >
                  <td className="px-3 py-2 text-swiss-stone text-[10px] font-medium">
                    {sample.label}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-swiss-charcoal">
                    {sample.source_timestamp}
                  </td>
                  <td className="px-3 py-2 text-right text-swiss-charcoal">
                    {renderValueCell(sample)}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-swiss-charcoal">
                    {sample.db_timestamp}
                  </td>
                  <td className="px-3 py-2 text-right text-swiss-charcoal">
                    {renderDbValueCell(sample)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {sample.match ? (
                      <span className="text-brand-primary font-bold text-sm">✓</span>
                    ) : (
                      <span className="text-swiss-stone font-bold text-sm">✗</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DST Validation */}
      {validation.dst_validation && (
        <div className="space-y-2">
          <div className="text-[10px] font-medium text-swiss-stone uppercase tracking-wider">
            {t(`${type}Upload.validation.dstCriticalHours`)}
          </div>
          <div className="grid grid-cols-1 gap-2">
            {Object.entries(validation.dst_validation).map(([key, val]) => (
              <div key={key} className="p-2 rounded border text-xs bg-white border-swiss-mist">
                <div className="flex items-center justify-between">
                  <span className="text-swiss-charcoal font-medium">{val.label}</span>
                  <span
                    className={`text-[10px] font-semibold ${
                      val.status === 'OK' ? 'text-brand-primary' : 'text-swiss-stone'
                    }`}
                  >
                    {val.status}
                  </span>
                </div>
                <div className="text-[10px] text-swiss-stone mt-1">
                  {t(`${type}Upload.validation.file`)}: {val.source_count} •{' '}
                  {t(`${type}Upload.validation.db`)}: {val.db_count}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
